import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios.js';
import { useHistory, useParams } from 'react-router-dom';
import { useToastify } from '../../../hooks/useToastify';
import { Button } from 'app/components';

type Params = {
  profileId: string;
};

const labelPermissions = {
  dashboard: "Dashboard",
  users: "Usuários",
  finance: "Financeiro",
  clients: "Clientes",
  profile: "Perfil",
  reports: "Relatórios",
  ads: "Anúncios",
  partners: "Parceiros",
  plans: "Planos",
  gifts: "Gifts",
  system_log: "LOG",
}

const labelActionPermissions = {
  create: "Criar",
  edit: "Editar",
  delete: "Excluir",
  view: "Visualizar",
}

function PerfilConfig() {
  const { profileId } = useParams<Params>();
  const [resources, setResources] = useState([]);
  const [actions, setActions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const toast = useToastify();
  const history = useHistory();

  useEffect(() => {
    fetchPermissionsData();
  }, [profileId]);

  const fetchPermissionsData = async () => {
    try {
      const resourcesResponse = await axios.get('/perfil/resources');  // Exemplo: GET /api/resources
      const actionsResponse = await axios.get('/perfil/actions');      // Exemplo: GET /api/actions
      const rolePermissionsResponse = await axios.get(`/perfil/${profileId}/permissions`);  // Exemplo: GET /api/roles/{roleId}/permissions

      setResources(resourcesResponse.data.resources);
      setActions(actionsResponse.data.actions);

      const permissions = rolePermissionsResponse.data.permissions.map(pem => ({
        resourceId: pem.resourceId,
        actionId: pem.actionId,
      }));

      setRolePermissions(permissions ?? []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setLoading(false);
    }
  };

  const handlePermissionChange = (resourceId: Number, actionId: Number, checked: Boolean) => {

    const  prevPermissions = rolePermissions.filter(pem => !(pem.resourceId === resourceId && pem.actionId === actionId));
    
    if (checked) {
      setRolePermissions([
        ...prevPermissions,
        {
          resourceId,
          actionId
        }
      ]);
    } else {
      setRolePermissions([
        ...prevPermissions
      ]);
    }
  };

  // Função para marcar ou desmarcar todas as permissões de um recurso
  const handleToggleAllPermissions = (resourceId, checked) => {
    const updatedPermissions = [];

    const prevPermissions = rolePermissions.filter(pem => !(pem.resourceId === resourceId));

    if (checked) {
      actions.forEach((action) => {
        updatedPermissions.push({
          resourceId,
          actionId: action.id
        });
      });
    }

    setRolePermissions([
      ...prevPermissions,
      ...updatedPermissions
    ]);
  };

  const handleSave = async () => {
    try {
      const retorno = await axios.put(`/perfil/${profileId}/permissions`, {
        permissions: rolePermissions
      });

      if (retorno.status !== 200) {
        throw Error(retorno.statusText)
      }

      // fetchPermissionsData();

      toast.show('success', 'Permissões salvas com sucesso!', () => window.location.reload())
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      toast.show('error', 'Erro ao salvar permissões.')
    }
  };

  if (loading) {
    return (
      <div className="tw-w-full tw-px-6 tw-pb-6">
        <p>Carregando permissões...</p>
      </div>
    )
  }

  return (
    <div className="tw-w-full tw-px-6 tw-pb-6">
      <div className="tw-p-6 tw-bg-white tw-shadow-md tw-rounded-lg">

        <div className='tw-flex tw-justify-between tw-mb-10 tw-mt-4'>
          <h2 className="tw-text-xl tw-font-bold ">Configurar Permissões para o Perfil</h2>
          <div className="tw-flex tw-justify-end tw-space-x-4">
            <div className="tw-flex tw-justify-end tw-space-x-4">
              <Button.Element variant="secondary" onClick={() => history.goBack()}>
                <Button.Text text='Voltar' variant='secondary' />
              </Button.Element>
              <Button.Element variant="primary" onClick={handleSave}>
                <Button.Text text='Salvar' variant='primary' />
              </Button.Element>
            </div>
          </div>
        </div>

        <Box
          className="tw-w-full tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-flex-wrap tw-gap-8 tw-items-start"
        >
          {resources.map((resource) => {
            const allSelected = actions.every((action) => rolePermissions.find(pem => pem.resourceId == resource.id && pem.actionId == action.id));
            const indeterminate = rolePermissions.find(pem => pem.resourceId == resource.id)

            return (
              <Accordion key={resource.id}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel-${resource.id}-content`}
                  id={`panel-${resource.id}-header`}
                  className="tw-bg-gray-100"
                >
                  <span 
                    className='tw-text-azulescuro tw-font-medium'
                  >
                    {labelPermissions[resource.name]}
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  <FormGroup row>
                    <Box className="tw-flex tw-flex-col tw-w-full">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allSelected}
                            indeterminate={
                              !allSelected && indeterminate
                            }
                            onChange={(e) => handleToggleAllPermissions(resource.id, e.target.checked)}
                          />
                        }
                        label="Todos"
                      />
                      <div className='flex'>
                        {actions.map((action) => (
                          <FormControlLabel
                            key={action.id}
                            control={
                              <Checkbox
                                checked={rolePermissions.find(pem => pem.resourceId == resource.id && pem.actionId == action.id) ?? false}
                                onChange={(e) => handlePermissionChange(resource.id, action.id, e.target.checked)}
                              />
                            }
                            label={labelActionPermissions[action.name]}
                          />
                        ))}
                      </div>
                    </Box>
                  </FormGroup>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      </div>
    </div>
  );
}

export default PerfilConfig;
